import type { EventRow, RunSummary, SceneStat, TurnRow } from './types';
import { dartRound } from './types';

/** 목록 · 타임라인 4. */

const FALLBACK_DATE = new Date(2026, 0, 1); // Dart `DateTime(2026)` 짝

/**
 * 장면 통계.
 *
 * ⚠️ **완주율 오름차순으로 정렬**한다 — 비율을 그리는 목록은 그 비율 순이어야
 *    막대가 오르내리지 않는다.
 */
export function lensSceneStats(events?: EventRow[] | null): SceneStat[] {
  const chapters = new Map<string, string>();
  const entered = new Map<string, number>();
  const completed = new Map<string, number>();
  const turns = new Map<string, number>();

  for (const e of events ?? []) {
    const id = e.sceneId;
    if (id === '') continue;
    chapters.set(id, e.chapterId);
    if (e.eventName === 'scene_entered') entered.set(id, (entered.get(id) ?? 0) + 1);
    if (e.eventName === 'scene_completed') {
      completed.set(id, (completed.get(id) ?? 0) + 1);
      turns.set(id, (turns.get(id) ?? 0) + e.turnCount);
    }
  }

  const pctOf = (id: string): number => {
    const n = entered.get(id) ?? 0;
    if (n === 0) return 0;
    return ((completed.get(id) ?? 0) * 100) / n;
  };

  const ids = [...entered.keys()].sort((a, b) => {
    const ra = pctOf(a);
    const rb = pctOf(b);
    return ra === rb ? 0 : ra < rb ? -1 : 1;
  });

  return ids.map((id) => {
    const pct = pctOf(id);
    const c = completed.get(id) ?? 0;
    const avg = c === 0 ? 0 : (turns.get(id) ?? 0) / c;
    return {
      sceneId: id,
      chapterId: chapters.get(id) ?? '',
      enteredText: `${entered.get(id)}`,
      completedText: `${c}`,
      completionPctText: `${pct.toFixed(1)}%`,
      completionPct: pct,
      avgTurnsText: avg === 0 ? '—' : `${avg.toFixed(1)}턴`,
      dropText: `${(entered.get(id) ?? 0) - c}`,
      tone: pct >= 90 ? 'good' : pct >= 80 ? 'warn' : 'bad',
    };
  });
}

/**
 * 회차 요약.
 *
 * ⚠️ `costText` 는 `cost_usd` 가 없으면 `-` 다 — **지어내지 않는다.**
 */
export function lensRunSummariesV2(
  events?: EventRow[] | null,
  _costPerTurnKrw?: number | null,
  usdKrwRate?: number | null,
  costFeeRate?: number | null,
): RunSummary[] {
  const exitLabels: Record<string, string> = {
    broken_transition: '끊긴 전이(저작 오류)',
    captured: '붙잡힘',
    app_closed: '앱 종료',
    timeout: '타임아웃',
    error: '오류',
    abandoned: '이탈',
  };
  const krwPerUsd = (usdKrwRate ?? 0) * (1 + (costFeeRate ?? 0) / 100);
  const started = new Map<string, Date>();
  const usd = new Map<string, number>();
  const turns = new Map<string, number>();
  const duration = new Map<string, number>();
  const blocked = new Map<string, number>();
  const ended = new Set<string>();
  const lastExit = new Map<string, string>();

  for (const e of events ?? []) {
    const id = e.runId;
    if (id === '') continue;
    if (e.costUsd > 0) usd.set(id, (usd.get(id) ?? 0) + e.costUsd);
    switch (e.eventName) {
      case 'run_started':
        started.set(id, e.occurredAt ?? FALLBACK_DATE);
        break;
      case 'scene_completed':
        turns.set(id, (turns.get(id) ?? 0) + e.turnCount);
        duration.set(id, (duration.get(id) ?? 0) + e.durationSec);
        break;
      case 'input_blocked':
      case 'unsupported_action_requested':
        blocked.set(id, (blocked.get(id) ?? 0) + 1);
        break;
      case 'action_interpreted':
        if (e.parseOk === false) blocked.set(id, (blocked.get(id) ?? 0) + 1);
        break;
      case 'ending_reached':
        ended.add(id);
        break;
      case 'scene_exited':
        if (e.exitReason !== '' && e.exitReason !== 'completed') {
          lastExit.set(id, e.exitReason);
        }
        break;
    }
  }

  const two = (n: number): string => String(n).padStart(2, '0');
  const ids = [...started.keys()].sort(
    (a, b) => started.get(b)!.getTime() - started.get(a)!.getTime(),
  );

  return ids.map((id) => {
    const t = turns.get(id) ?? 0;
    const cost = (usd.get(id) ?? 0) > 0 ? usd.get(id)! * krwPerUsd : -1;
    const secs = duration.get(id) ?? 0;
    const at = started.get(id)!;
    const done = ended.has(id);
    const exit = lastExit.get(id);
    return {
      runId: id,
      // ⚠️ **지역 시각이다** — Dart `DateTime` 의 month/day/hour/minute 와 같다.
      startedAtText: `${at.getMonth() + 1}/${at.getDate()} ${two(at.getHours())}:${two(at.getMinutes())}`,
      durationText: `${dartRound(secs / 60)}분`,
      turnCountText: `${t}턴`,
      endingText: done ? '완주' : ((exit === undefined ? undefined : exitLabels[exit]) ?? exit ?? '진행 중'),
      blockedText: `${blocked.get(id) ?? 0}건`,
      costText: cost < 0 ? '-' : `${dartRound(cost)}원`,
      tone: done ? 'good' : exit === 'error' ? 'bad' : 'warn',
    };
  });
}

const EVENT_LABELS: Record<string, string> = {
  run_started: '회차 시작',
  scene_entered: '장면 진입',
  action_interpreted: '행동 해석',
  input_blocked: '입력 차단',
  unsupported_action_requested: '미지원 행동',
  narration_validation_failed: '서술 검증 실패',
  scene_completed: '장면 완료',
  scene_exited: '장면 이탈',
  ending_reached: '엔딩 도달',
  check_resolved: '판정',
  flag_acquired: '플래그 획득',
  hidden_tag_leaked: '숨은 태그 누설',
};
const EXIT_LABELS: Record<string, string> = {
  completed: '완주',
  broken_transition: '끊긴 전이(저작 오류)',
  captured: '붙잡힘',
  app_closed: '앱 종료',
  timeout: '타임아웃',
  error: '오류',
  abandoned: '이탈',
};
const FALLBACK_LABELS: Record<string, string> = {
  call_failed: '호출 실패',
  parse_failed: '파싱 실패',
};
const TONE_OF: Record<string, string> = {
  input_blocked: 'bad',
  narration_validation_failed: 'bad',
  hidden_tag_leaked: 'bad',
  unsupported_action_requested: 'warn',
  ending_reached: 'good',
  scene_completed: 'good',
  flag_acquired: 'good',
};

/**
 * ★★★ 「무엇이 문제인가」는 **이 한 곳**에 있다.
 * ⚠️ `lensTurnProblemSummary` 가 같은 판정을 쓴다 — **한쪽만 고치면 「문제
 * 2건」이라 써 놓고 5줄을 보여준다.**
 * ★ 굴림 실패는 문제가 아니다(정상 진행).
 */
function isProblem(e: EventRow): boolean {
  switch (e.eventName) {
    case 'input_blocked':
    case 'unsupported_action_requested':
    case 'narration_validation_failed':
    case 'hidden_tag_leaked':
      return true;
    case 'action_interpreted':
      return e.parseOk === false || e.fallbackReason !== '';
    case 'scene_exited':
      return e.exitReason !== '' && e.exitReason !== 'completed';
    default:
      return false;
  }
}

/**
 * 턴 타임라인. `mode === 'all'` 이면 전부, 아니면 **문제 턴만**.
 *
 * ★ **번호(`seq`)는 거르기 전 기준**이다 — 다시 매기면 「25줄 중 11번째」라는
 *   정보가 사라진다.
 */
export function lensTurnTimelineV2(
  events?: EventRow[] | null,
  runId?: string | null,
  mode?: string | null,
): TurnRow[] {
  const showAll = (mode ?? '') === 'all';
  const target = runId ?? '';
  const rows: TurnRow[] = [];
  let seq = 0;

  for (const e of events ?? []) {
    if (e.runId !== target) continue;
    seq++;
    if (!showAll && !isProblem(e)) continue;
    const at = e.occurredAt ?? FALLBACK_DATE;
    const mm = String(at.getMinutes()).padStart(2, '0');

    let detail = '';
    switch (e.eventName) {
      case 'action_interpreted':
        detail = e.parseOk === false ? '해석 실패' : '해석 성공';
        if (e.fallbackReason !== '') {
          detail += ` · 폴백(${FALLBACK_LABELS[e.fallbackReason] ?? e.fallbackReason})`;
        }
        break;
      case 'unsupported_action_requested':
        detail =
          e.judgmentKind === 'reject_impossible' ? '규칙상 불가능'
          : e.judgmentKind === 'reject_unclear' ? '의도 불명확'
          : 'allowed_actions 에 없음';
        break;
      case 'check_resolved': {
        const ok = e.success === true ? '성공' : '실패';
        if (e.kind !== '' && e.kind !== 'roll' && e.kind !== 'bypass') {
          detail = `판정 없음 · ${e.kind}`;
        } else if (e.kind === 'bypass') {
          detail = `우회 · ${ok}`;
        } else {
          detail = `${e.rollResult} / DC ${e.dc} · ${ok}`;
        }
        break;
      }
      case 'flag_acquired':
        detail = e.flagKind === 'story' ? `이야기 · ${e.flagId}` : `판정 · ${e.flagId}`;
        break;
      case 'hidden_tag_leaked':
        detail = `${e.leakedTag} · ${e.propId}`;
        break;
      case 'ending_reached':
        detail = e.endingId === '' ? '' : e.endingId;
        break;
      case 'narration_validation_failed':
        detail = e.reason;
        if (e.usedFallback === true) detail += ' · 폴백 사용';
        else if (e.regenerated === true) detail += ' · 재생성';
        break;
      case 'scene_completed':
        detail = `${e.turnCount}턴 · ${e.durationSec}초`;
        break;
      case 'scene_exited':
        detail = EXIT_LABELS[e.exitReason] ?? e.exitReason;
        break;
    }

    let tone: string;
    if (e.eventName === 'action_interpreted') {
      tone = e.parseOk === false ? 'bad' : e.fallbackReason !== '' ? 'warn' : 'neutral';
    } else if (e.eventName === 'scene_exited') {
      tone = e.exitReason !== '' && e.exitReason !== 'completed' ? 'warn' : 'neutral';
    } else {
      tone = TONE_OF[e.eventName] ?? 'neutral';
    }

    rows.push({
      seq,
      timeText: `${at.getHours()}:${mm}`,
      eventLabel: EVENT_LABELS[e.eventName] ?? e.eventName,
      sceneId: e.sceneId,
      verb: e.actionType,
      detail,
      tone,
    });
  }
  return rows;
}

/**
 * 문제 턴 요약.
 *
 * ⚠️ **걸러낸 건수를 반드시 밝힌다** — 안 밝히면 사고가 없었던 것인지 화면이
 * 숨긴 것인지 구분할 수 없다.
 */
export function lensTurnProblemSummary(
  events?: EventRow[] | null,
  runId?: string | null,
): string {
  const target = runId ?? '';
  let total = 0;
  let parseFail = 0;
  let rejected = 0;
  let blocked = 0;
  let narration = 0;
  let fallback = 0;
  let leaked = 0;
  let badExit = 0;

  for (const e of events ?? []) {
    if (e.runId !== target) continue;
    total++;
    switch (e.eventName) {
      case 'input_blocked': blocked++; break;
      case 'unsupported_action_requested': rejected++; break;
      case 'narration_validation_failed': narration++; break;
      case 'hidden_tag_leaked': leaked++; break;
      case 'action_interpreted':
        if (e.parseOk === false) parseFail++;
        if (e.fallbackReason !== '') fallback++;
        break;
      case 'scene_exited':
        if (e.exitReason !== '' && e.exitReason !== 'completed') badExit++;
        break;
    }
  }

  const parts: string[] = [];
  if (parseFail > 0) parts.push(`해석 실패 ${parseFail}`);
  if (rejected > 0) parts.push(`거부 ${rejected}`);
  if (blocked > 0) parts.push(`입력 차단 ${blocked}`);
  if (narration > 0) parts.push(`서술 검증 실패 ${narration}`);
  if (fallback > 0) parts.push(`폴백 ${fallback}`);
  if (leaked > 0) parts.push(`태그 누설 ${leaked}`);
  if (badExit > 0) parts.push(`비정상 이탈 ${badExit}`);

  if (total === 0) return '이 회차의 이벤트를 찾지 못했습니다.';
  const count = parseFail + rejected + blocked + narration + fallback + leaked + badExit;
  if (count === 0) {
    return `전체 ${total}줄 · 문제 턴 없음. "전체"로 바꾸면 모든 줄이 보입니다.`;
  }
  return `전체 ${total}줄 중 문제 ${count}건 — ${parts.join(' · ')}`;
}
